import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, RefreshCw, Sun, Search, Trash2, Download } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-yellow-100 text-yellow-800 border-yellow-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  converted: "bg-green-100 text-green-800 border-green-200",
  dropped: "bg-stone-100 text-stone-700 border-stone-200",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchesStatus = status === "all" || l.status === status;
      const needle = q.trim().toLowerCase();
      const haystack = [l.name, l.phone, l.email, l.pincode, l.segment]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesStatus && (!needle || haystack.includes(needle));
    });
  }, [leads, q, status]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/leads/${id}`, { status: newStatus });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
      toast.success("Status updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error("No leads to export");
      return;
    }
    const headers = [
      "Name",
      "Phone",
      "Email",
      "Pincode",
      "State",
      "DISCOM",
      "Segment",
      "Source",
      "Estimated kW",
      "Monthly Units",
      "Monthly Bill",
      "Status",
      "Message",
      "Created At",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filtered.map((l) =>
      [
        l.name,
        l.phone,
        l.email,
        l.pincode,
        l.state,
        l.discom,
        l.segment,
        l.source,
        l.estimated_kw,
        l.monthly_units,
        l.monthly_bill,
        l.status,
        l.message,
        l.created_at,
      ]
        .map(esc)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `brightline-leads-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads`);
  };

  const stats = useMemo(() => {
    const total = leads.length;
    const byStatus = leads.reduce(
      (acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }),
      {}
    );
    const thisMonth = leads.filter(
      (l) => new Date(l.created_at).getMonth() === new Date().getMonth()
    ).length;
    return { total, thisMonth, ...byStatus };
  }, [leads]);

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-white grid place-items-center p-1 border border-stone-200">
              <img src="/purvodaya-logo.png" alt="Purvodaya Solar" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-extrabold text-[16px] text-stone-900">
                Purvodaya <span className="text-orange-500">Admin</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-green-700">
                Leads Dashboard
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-sm text-stone-500">
              Hi, <span className="font-semibold text-stone-800">{user?.name || "Admin"}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              data-testid="admin-logout"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="Total Leads" value={stats.total} />
          <Stat label="This Month" value={stats.thisMonth} accent />
          <Stat label="New" value={stats.new || 0} />
          <Stat label="Contacted" value={stats.contacted || 0} />
          <Stat label="Converted" value={stats.converted || 0} />
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 overflow-hidden">
          <div className="p-4 md:p-5 border-b border-stone-100 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, phone, pincode…"
                  className="pl-9"
                  data-testid="admin-search"
                />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-[140px]" data-testid="admin-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="dropped">Dropped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 self-start">
              <Button
                size="sm"
                variant="outline"
                onClick={exportCsv}
                data-testid="admin-export-csv"
                className="rounded-full bg-green-50 border-green-200 text-green-800 hover:bg-green-100"
              >
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchLeads}
                data-testid="admin-refresh"
                className="rounded-full"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone / Email</TableHead>
                  <TableHead>Segment</TableHead>
                  <TableHead>System</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-stone-500 py-12">
                      Loading leads…
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-stone-500 py-12">
                      No leads yet. New enquiries will appear here in real time.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell>
                        <div className="font-semibold text-stone-900">{l.name}</div>
                        {l.pincode ? (
                          <div className="text-xs text-stone-500">
                            PIN {l.pincode}
                            {l.state ? ` · ${l.state}` : ""}
                          </div>
                        ) : null}
                        {l.discom ? (
                          <div className="text-[11px] text-green-700 mt-0.5">
                            {l.discom}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{l.phone}</div>
                        {l.email ? (
                          <div className="text-xs text-stone-500">{l.email}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="capitalize text-sm">{l.segment}</TableCell>
                      <TableCell>
                        {l.estimated_kw ? (
                          <div className="text-sm">
                            <span className="font-semibold">{l.estimated_kw} kW</span>
                            {l.monthly_units ? (
                              <span className="text-xs text-stone-500 block">
                                {l.monthly_units} u/m · ₹{l.monthly_bill}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-stone-400 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-stone-500">
                        {l.source || "website"}
                      </TableCell>
                      <TableCell className="text-xs text-stone-500 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={l.status}
                          onValueChange={(v) => updateStatus(l.id, v)}
                        >
                          <SelectTrigger
                            className="h-8 w-[130px] border-0 p-0"
                            data-testid={`lead-status-${l.id}`}
                          >
                            <Badge
                              variant="outline"
                              className={`capitalize ${STATUS_COLORS[l.status] || ""}`}
                            >
                              {l.status}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="dropped">Dropped</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeLead(l.id)}
                          data-testid={`lead-delete-${l.id}`}
                          className="text-red-600 hover:bg-red-50"
                          aria-label="Delete lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        accent ? "bg-green-600 border-green-700 text-white" : "bg-white border-stone-200"
      }`}
    >
      <div
        className={`text-[11px] uppercase tracking-wider ${
          accent ? "opacity-80" : "text-stone-500"
        }`}
      >
        {label}
      </div>
      <div className="font-display font-extrabold text-3xl mt-1 digit-stat">
        {value}
      </div>
    </div>
  );
}
